import { useModalForm, useTable } from '@refinedev/antd';
import {
  BaseRecord,
  CrudFilters,
  getDefaultFilter
} from '@refinedev/core';
import { Button, Table } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DeleteOutlined } from '@ant-design/icons';
import { ColumnType } from 'antd/es/table';
import { useModalForm as useModalFormAntd } from 'sunflower-antd';
import { NewBankModalCreate } from '../../../../components/modal/admin/addBank';
import { EditBank } from '../../../../components/modal/admin/editBank';
import { useDeleteBank } from '../../../../utils/hooks/useDeleteBank.hook';
import { useIsAdminRole } from '../../../../utils/hooks/useIdentityAdmin.hook';
import { useNotificationOkCancel } from '../../../../utils/hooks/useNotificationOkCancel.hook';
import { onSearch } from '../../../../utils/list.utils';
import { ButtonListHeader } from '../../../components/buttonsList';
import { confirmModalDeleteProps } from '../../../components/constants';
import { Row } from '../../../components/interface';
import { TableForm } from '../../../crypto-withdrawal/tableForm';

interface IProps {
  customerId?: number;
  isActive?: boolean;
}

export const CustomerBanks: React.FC<IProps> = ({ customerId, isActive = true }) => {
  const [editingId, setEditingId] = useState<number>()
  const [deletingId, setDeletingId] = useState<number>()
  const isAdminRole = useIsAdminRole()

  // Create permanent filter for customerId
  const permanentFilter = useMemo<CrudFilters>(() => {
    if (!customerId) return [];
    return [
      {
        field: '_userId',
        operator: 'eq',
        value: customerId,
      },
    ];
  }, [customerId]);

  const {
    tableProps,
    searchFormProps,
    filters,
    setCurrent,
    tableQueryResult: { refetch: refetchList },
  } = useTable<Row>({
    resource: 'api/v1/banks/no_default_accounts',
    syncWithLocation: true,
    pagination: {
      pageSize: 10,
    },
    onSearch,
    permanentFilter,
    queryOptions: {
      enabled: isActive && !!customerId,
    },
  })

  const {
    modalProps: createBankModalProps,
    formProps: createBankFormProps,
    show: createBankModalShow,
    formLoading: createBankFormLoading,
    submit: createBankFormSubmit,
    close: createBankModalClose,
  } = useModalForm({
    warnWhenUnsavedChanges: false,
    action: 'create',
    resource: 'api/v1/banks',
  })

  const {
    close: editModalClose,
    modalProps: editModalProps,
    formProps: editFormProps,
    show: editModalShow,
  } = useModalFormAntd({})

  const { deleteBank } = useDeleteBank()

  const reloadListAfterDelete = useCallback(async () => {
    const result = await refetchList()
    const pagination = tableProps.pagination
    if (!pagination || typeof pagination === 'boolean') return

    const responseData: any = result?.data
    const pageSize = Number(pagination.pageSize || 10)
    const currentPage = Number(pagination.current || 1)
    const responseTotal = Number(
      responseData?.total ??
      responseData?.totalCount ??
      responseData?.data?.total ??
      responseData?.data?.totalCount
    )

    const total = Number.isNaN(responseTotal)
      ? Number(pagination.total || 0)
      : responseTotal
    const targetPage = Math.max(1, Math.ceil(total / pageSize))

    if (currentPage > targetPage) {
      if (setCurrent) {
        setCurrent(targetPage)
      } else {
        pagination.onChange?.(targetPage, pageSize)
      }
    }
  }, [refetchList, setCurrent, tableProps.pagination])

  const onOkDelete = useCallback(async () => {
    if (deletingId !== undefined) {
      setDeletingId(undefined)
      await deleteBank(deletingId)
      await reloadListAfterDelete()
    }
  }, [deletingId, deleteBank, reloadListAfterDelete])

  const onCancelDelete = useCallback(() => {
    setDeletingId(undefined)
  }, [])

  const { openNotification, contextHolder } = useNotificationOkCancel({
    ...confirmModalDeleteProps,
    onOk: onOkDelete,
    onCancel: onCancelDelete,
  })

  const handleClickOnCell = (record: BaseRecord) => {
    return {
      onClick: () => {
        if (record.id && isAdminRole) {
          setEditingId(Number(record.id))
        }
      },
    }
  }

  useEffect(() => {
    if (deletingId !== undefined) {
      openNotification()
    }
  }, [deletingId, openNotification])

  const columns: ColumnType<Row>[] = [
    {
      dataIndex: 'id',
      title: 'ID',
      onCell: handleClickOnCell,
      sorter: true,
    },
    {
      dataIndex: 'name',
      title: 'Name',
      onCell: handleClickOnCell,
      sorter: true,
    },
    {
      dataIndex: 'swift',
      title: 'SWIFT',
      onCell: handleClickOnCell,
    },
    {
      dataIndex: 'legalAddress',
      title: 'Legal Address',
      onCell: handleClickOnCell,
    },
    {
      dataIndex: 'country',
      title: 'Country',
      onCell: handleClickOnCell,
    },
    {
      render: (value, data) => {
        return (
          isAdminRole && (
            <Button
              className={'list-button list-button--delete'}
              icon={<DeleteOutlined style={{ fontSize: '19px' }} />}
              size='small'
              onClick={() => setDeletingId(data.id)}
            />
          )
        )
      },
    },
  ]

  return (
    <>
      <div>
        <TableForm
          {...searchFormProps}
          initialValues={{
            searchValue: getDefaultFilter('', filters),
          }}
          onSubmit={searchFormProps.form?.submit}
          searchPlaceholder={'Search banks by SWIFT or name'}
          showDateFilter={false}
          headerButtons={
            isAdminRole ? ButtonListHeader(createBankModalShow) : null
          }
        />
        <Table
          {...tableProps}
          rowKey='id'
          columns={columns as any}
          dataSource={tableProps.dataSource}
          loading={tableProps.loading}
        />
      </div>
      {contextHolder}
      <NewBankModalCreate
        closeModal={createBankModalClose}
        formProps={createBankFormProps}
        modalProps={createBankModalProps}
        formIsLoading={createBankFormLoading}
        formSubmit={createBankFormSubmit}
        refetchList={refetchList}
        customerId={customerId}
        isCustomer={true}
      />
      <EditBank
        editingId={editingId}
        changeEditingId={setEditingId}
        show={editModalShow}
        formProps={editFormProps}
        modalProps={editModalProps}
        refetchList={refetchList}
        closeModal={editModalClose}
        isCustomer={true}
        userId={customerId}
      />
    </>
  )
}


